import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSetAtom } from "jotai";
import { projectGatingModeAtom } from "@/atoms/gatingAtoms";
import CaseViewer from "@/components/CaseViewer";
import { CASE_HEADER } from "@/components/devicePanel/panelLayout";
import { PanelRailSkeleton } from "@/components/devicePanel/PanelSkeleton";
import { JointReviewLoadError } from "@/components/jointReview/JointReviewLoadError";
import { JointReviewRail, type Selection } from "@/components/jointReview/JointReviewRail";
import {
  jointReviewImagesUrl,
  useJointReviewProject,
  useJointReviewStudy,
  usePointer,
  useSetPointer,
} from "@/hooks/useJointReview";
import {
  caseLabel,
  caseUrl,
  doneCount,
  freshDone,
  listUrl,
  nextUndone,
  reviewSeries,
  sharesState,
  voices,
  type JointReviewProject,
  type JointReviewStudy,
} from "@/types/jointReview";
import { GATING_MODE } from "@/types/gating";

/** The item a study opens on: the linked one, else the shared pointer when
 * following and it is here, else the first item not done, else the first. */
function openingItem(
  study: JointReviewStudy,
  linked: string | null,
  pointed: string | null,
): string | null {
  const here = (itemId: string | null) =>
    itemId !== null && study.items.some((item) => item.item_id === itemId);
  if (here(linked)) return linked;
  if (here(pointed)) return pointed;
  return (study.items.find((item) => !item.done) ?? study.items[0])?.item_id ?? null;
}

/** Progress in the header, and the line shown once nothing is left for this reader. */
function progressText(project: JointReviewProject, done: number) {
  if (project.phase === "independent")
    return {
      progress: `${done} of ${project.total_items} called`,
      allDone: project.other_done
        ? "Both readers have called every item."
        : "You have called every item. The joint session opens when the other reader finishes.",
    };
  const agreed = project.agreed_items === null ? "" : ` · ${project.agreed_items} agreed`;
  return {
    progress: `${done} of ${project.total_items} final${agreed}`,
    allDone: "Every item is final.",
  };
}

/**
 * One study of a joint review: the images with both readers' round-1 marks (or
 * a blind review's markers), and the joint review rail. Both readers work here
 * at once. The item either reader selects or reaches with Next becomes the
 * shared pointer; with Follow on (the default), a reader whose pointer the other
 * moved goes to that item, in this study or another. In a blind review's
 * independent phase nothing is shared: no pointer and no Follow. `?item=` opens
 * on an item. An admin observing follows the readers and never moves the pointer.
 */
const JointReviewCasePage: React.FC = () => {
  const { projectId, studyUid } = useParams<{
    projectId: string;
    studyUid: string;
  }>();
  const [params] = useSearchParams();
  const linkedItem = params.get("item");
  const navigate = useNavigate();
  const project = useJointReviewProject(projectId);
  const study = useJointReviewStudy(projectId, studyUid);
  const shared = sharesState(project.data);
  const pointer = usePointer(projectId, shared);
  // The other reader's position, only where the review shares it.
  const otherPointer = shared ? pointer.data : null;
  const setPointer = useSetPointer(projectId!);
  const [follow, setFollow] = useState(true);
  const [selection, setSelection] = useState<Selection>({
    studyUid: null,
    itemId: null,
    nonce: 0,
  });

  // The round-1 reads were view-dependent stone studies; set it here too so a
  // gating mode left by another page can't change the grid.
  const setGatingMode = useSetAtom(projectGatingModeAtom);
  useEffect(() => setGatingMode(GATING_MODE.VIEW_DEPENDENT), [setGatingMode]);

  const observer = !!project.data?.observer;
  const select = (itemId: string, share: boolean) => {
    setSelection((previous) => ({
      studyUid: studyUid!,
      itemId,
      nonce: previous.nonce + 1,
    }));
    if (share && shared && !observer) setPointer.mutate({ study_uid: studyUid!, item_id: itemId });
  };

  // Open each study on one of its items, once its items have loaded (and only then).
  const items = study.data?.items;
  const selectionHere = selection.studyUid === studyUid;
  useEffect(() => {
    if (!study.data || selectionHere) return;
    const other = otherPointer;
    const pointed = follow && other && other.study_uid === studyUid ? other.item_id : null;
    const itemId = openingItem(study.data, linkedItem, pointed);
    // A link comes from a Next or a follow, which already set the pointer.
    if (itemId) select(itemId, linkedItem === null && pointed === null);
  }, [study.data, selectionHere]);

  // Follow the other reader: go wherever they last moved the pointer. Runs on a
  // pointer, switch or study change only.
  const followed = useRef<string | null>(null);
  useEffect(() => {
    if (!follow) followed.current = null;
    const other = otherPointer;
    if (!follow || !other || other.set_by_me || followed.current === other.updated_at) return;
    followed.current = other.updated_at;
    if (other.study_uid !== studyUid) navigate(caseUrl(projectId!, other.study_uid, other.item_id));
    else if (other.item_id && (!selectionHere || other.item_id !== selection.itemId))
      select(other.item_id, false);
  }, [follow, otherPointer, studyUid]);

  if (project.isError || study.isError)
    return (
      <JointReviewLoadError
        error={project.error ?? study.error}
        onRetry={() => {
          project.refetch();
          study.refetch();
        }}
      />
    );

  const fresh = freshDone(studyUid!, items);
  const next = () => {
    if (!project.data) return;
    const from =
      selectionHere && selection.itemId
        ? { study_uid: studyUid!, item_id: selection.itemId }
        : null;
    const target = nextUndone(project.data, from, fresh);
    if (!target) return;
    if (target.study_uid === studyUid) {
      select(target.item_id, true);
      return;
    }
    if (shared && !observer) setPointer.mutate(target);
    navigate(caseUrl(projectId!, target.study_uid, target.item_id));
  };

  const status = project.data && progressText(project.data, doneCount(project.data, fresh));

  return (
    <div className="flex h-full flex-col">
      <div className={CASE_HEADER}>
        <Link to={listUrl(projectId!)} className="shrink-0 text-fg-muted hover:text-fg">
          ← Joint review
        </Link>
        {project.data && (
          <span className="font-medium text-fg">{caseLabel(project.data, studyUid!)}</span>
        )}
      </div>
      <div className="min-h-0 flex-1">
        {/* Mounted once the study is in, so the grid opens straight on the
            marks' series instead of laying out the panes twice. */}
        {study.data ? (
          <CaseViewer
            key={studyUid}
            studyData={{ studyId: studyUid! }}
            imagesUrl={jointReviewImagesUrl(projectId!, studyUid!)}
            segmentationLayout
            prioritySeriesId={reviewSeries(study.data)}
            rightRail={(jumpToSlice) =>
              study.data && project.data ? (
                <JointReviewRail
                  projectId={projectId!}
                  studyUid={studyUid!}
                  study={study.data}
                  selection={selection}
                  progress={status!.progress}
                  allDone={nextUndone(project.data, null, fresh) === null ? status!.allDone : null}
                  shared={shared}
                  follow={follow}
                  onFollowChange={setFollow}
                  onSelect={(itemId) => select(itemId, true)}
                  onNext={next}
                  jumpToSlice={jumpToSlice}
                  voices={voices(project.data)}
                />
              ) : (
                <PanelRailSkeleton />
              )
            }
          />
        ) : null}
      </div>
    </div>
  );
};

export default JointReviewCasePage;
